import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DashboardWidget } from '../services/dashboard';
import WidgetRenderer from './WidgetRenderer';

interface SectionInfo {
  id: string;
  label: string;
}

interface SortableWidgetProps {
  widget: DashboardWidget;
  householdId: string;
  editMode: boolean;
  onDeleteWidget?: (widgetId: string) => void;
  onUpdateWidget?: (widgetId: string, updates: Partial<DashboardWidget>) => void;
  onConfigureWidget?: (widgetId: string) => void;
  onMoveToSection?: (widgetId: string, sectionId: string | null, columnIndex?: number) => void;
  onExpandWidget?: (widgetId: string) => void; // Expand widget to fullscreen
  availableSections?: SectionInfo[];
  allWidgets?: DashboardWidget[]; // For nested sections to find their children
  isInSection?: boolean; // Whether this widget is inside a section
  children?: React.ReactNode; // Optional custom content (for sections with their own rendering)
}

/**
 * SortableWidget - Unified sortable wrapper for all widgets.
 * Works for both root-level widgets and widgets inside section columns.
 */
const SortableWidget: React.FC<SortableWidgetProps> = ({
  widget,
  householdId,
  editMode,
  onDeleteWidget,
  onUpdateWidget,
  onConfigureWidget,
  onMoveToSection,
  onExpandWidget,
  availableSections = [],
  allWidgets = [],
  isInSection = false,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: widget.id,
    disabled: !editMode,
    data: {
      type: 'widget',
      widget,
      isInSection,
    },
  });

  // Build style based on context
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  // For widgets in sections, use full width (columns determine width)
  // For root-level widgets, also full width (vertical stack)
  // Height is auto unless min_height is specified
  if (widget.min_height) {
    style.minHeight = `${widget.min_height}px`;
  }

  const className = [
    'sortable-widget',
    isDragging ? 'is-dragging' : '',
    isInSection ? 'in-section' : 'at-root',
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={className}
      {...attributes}
    >
      {children ? (
        // If children provided (e.g., SectionWidget), render those with drag listeners passed via clone
        React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement<{ dragListeners?: typeof listeners }>, { dragListeners: listeners })
          : children
      ) : (
        <WidgetRenderer
          widget={widget}
          householdId={householdId}
          editMode={editMode}
          onDelete={onDeleteWidget ? () => onDeleteWidget(widget.id) : undefined}
          onDeleteWidget={onDeleteWidget}
          onUpdateWidget={onUpdateWidget}
          onConfigure={onConfigureWidget ? () => onConfigureWidget(widget.id) : undefined}
          onConfigureWidget={onConfigureWidget}
          onMoveToSection={onMoveToSection}
          onExpand={onExpandWidget}
          availableSections={availableSections}
          allWidgets={allWidgets}
          isDragging={isDragging}
          isInSection={isInSection}
          // Pass sortable listeners to be attached to the drag handle
          dragListeners={listeners}
        />
      )}
    </div>
  );
};

export default SortableWidget;
